import type { SupportSettings, SupportSettingsUpdate } from "./types";

// In-memory mock of the two global-settings singletons, used until the real
// /api/support/settings/ endpoint is live. Mirrors the documented contract: a partial
// PATCH upserts the value and the same guards the backend enforces (non-negative price,
// http(s) URL) fire here, so mock mode exercises the real success/error paths.

// Tag a thrown error with a stable code; api.ts normalises it into a SettingsError.
function settingsError(code: string): Error & { settingsCode: string } {
  const err = new Error(code) as Error & { settingsCode: string };
  err.settingsCode = code;
  return err;
}

// Seeded with realistic values (matches the backend test fixtures) so the page renders
// populated in mock mode. Flip USE_MOCK off (VITE_USE_MOCK=false) to hit the real API.
const state: SupportSettings = {
  premium_price: "50000.00",
  support_telegram_link: "https://t.me/nasiyah_support",
  support_phone: "+998933852600",
  free_tier_months: 6,
};

export function mockGetSettings(): SupportSettings {
  return { ...state };
}

export function mockUpdateSettings(patch: SupportSettingsUpdate): SupportSettings {
  if (patch.premium_price !== undefined) {
    const n = Number(patch.premium_price);
    if (!Number.isFinite(n) || n < 0) throw settingsError("invalidPrice");
    // Normalise to two decimals like the backend DecimalField output.
    state.premium_price = n.toFixed(2);
  }
  if (patch.support_telegram_link !== undefined) {
    const link = patch.support_telegram_link.trim();
    if (link && !/^https?:\/\//i.test(link)) throw settingsError("invalidUrl");
    state.support_telegram_link = link || null;
  }
  if (patch.support_phone !== undefined) {
    const raw = patch.support_phone.trim();
    if (raw && !/^\+?[\d\s().-]+$/.test(raw)) throw settingsError("invalidPhone");
    const digits = raw.replace(/\D/g, "");
    const canonical = digits.length === 9 ? `+998${digits}` : `+${digits}`;
    if (raw && !/^\+[1-9]\d{7,14}$/.test(canonical)) {
      throw settingsError("invalidPhone");
    }
    state.support_phone = raw ? canonical : null;
  }
  if (patch.free_tier_months !== undefined) {
    const m = patch.free_tier_months;
    // Mirror the backend guard: a whole number of months, at least 1 (max 120).
    if (!Number.isInteger(m) || m < 1 || m > 120) throw settingsError("invalidMonths");
    state.free_tier_months = m;
  }
  return { ...state };
}
